import { auth } from '../firebase';
import { signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';

export const Auth = ({ onClose }) => {
    const [loading, setLoading] = useState(false);
    const [loginError, setLoginError] = useState(null);

    useEffect(() => {
        document.body.classList.add('modal-open');
        return () => document.body.classList.remove('modal-open');
    }, []);

    const signInWithGoogle = async () => {
        setLoading(true);
        setLoginError(null);
        
        try {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({
                prompt: 'select_account',
                hd: 'kamleon.com'
            });

            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Check user's email domain
            if (!user.email.endsWith('@kamleon.com')) {
                await signOut(auth);
                sessionStorage.clear();
                setLoginError('Invalid email domain. Please use a @kamleon.com email.');
                setLoading(false);
                return;
            }

            // Store user info in sessionStorage
            sessionStorage.setItem('user', JSON.stringify({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName
            }));
            
            const nameParts = (user.displayName || '').split(' ');
            sessionStorage.setItem('userName', nameParts[0] || '');
            sessionStorage.setItem('userLastName', nameParts.slice(1).join(' ') || '');
            sessionStorage.setItem('userRole', 'user');

            // Success - close modal ONLY after successful authentication
            onClose();
        } catch (error) {
            console.error('Google Sign-In error:', error);
            
            if (error.code === 'auth/popup-closed-by-user') {
                setLoginError('Sign-in cancelled.');
            } else if (error.code === 'auth/popup-blocked') {
                setLoginError('Popup blocked. Please allow popups for this site.');
            } else {
                setLoginError(error.message || 'Failed to sign in with Google. Please try again.');
            }
            setLoading(false);
        }
    };

    return (
        <Modal
            show={true}
            tabIndex="-1"
            onHide={() => {}} // Prevent closing by clicking outside or ESC
            backdrop="static"
            keyboard={false} // Disable ESC key
        >
            <Modal.Header>
                <div className="container">
                    <div className="row justify-content-center pt-5">
                        <div className="col-12 text-center">
                            <Modal.Title className='customBoldSoft'>Log In</Modal.Title>
                        </div>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className='px-5 pb-4 pt-4'>
                <div className="text-center mb-4">
                    <p className="text-muted">Sign in with your Kamleon Google account</p>
                </div>

                {/* Google Sign-In Button */}
                <Button 
                    variant="outline-dark" 
                    className="w-100 d-flex align-items-center justify-content-center"
                    onClick={signInWithGoogle}
                    disabled={loading}
                    style={{ padding: '12px' }}
                >
                    {loading ? (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"/>
                    ) : (
                        <>
                            <svg width="18" height="18" viewBox="0 0 18 18" className="me-2">
                                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.462.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
                            </svg>
                            <span>Sign in with Google</span>
                        </>
                    )}
                </Button>

                {loginError && (
                    <p className="text-danger mt-3 text-center" role="alert">{loginError}</p>
                )}
            </Modal.Body>
            <Modal.Footer className='mb-4'>
            </Modal.Footer>
        </Modal>
    );
};